import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, updateDoc, increment, collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../firebase/config';
import { Helmet } from 'react-helmet-async';
import { slugify, formatWhatsAppNumber } from '../utils/adUtils';
import Spinner from '../components/Spinner';
import AdBanner from '../components/AdBanner';
import { adConfig } from '../config/adConfig';
import './AdDetails.css';

const AdDetails = () => {
    // Toggle this condition to show or hide all ads on this page
    const showAdBanner = adConfig.showAdBanner;

    const { id, vendorSlug, adSlug } = useParams();
    const navigate = useNavigate();
    const [ad, setAd] = useState(null);
    const [seller, setSeller] = useState(null);
    const [loading, setLoading] = useState(true);
    const [currentImgIndex, setCurrentImgIndex] = useState(0);
    const [touchStart, setTouchStart] = useState(null);
    const [touchEnd, setTouchEnd] = useState(null);
    const [showFullImage, setShowFullImage] = useState(false);
    const [showFallbackAd, setShowFallbackAd] = useState(false);
    const hasViewed = useRef(false);

    // the required distance between touchStart and touchEnd to be detected as a swipe
    const minSwipeDistance = 50;

    const onTouchStart = (e) => {
        setTouchEnd(null);
        setTouchStart(e.targetTouches[0].clientX);
    };

    const onTouchMove = (e) => setTouchEnd(e.targetTouches[0].clientX);

    const onTouchEnd = () => {
        if (!touchStart || !touchEnd) return;
        const distance = touchStart - touchEnd;
        const isLeftSwipe = distance > minSwipeDistance;
        const isRightSwipe = distance < -minSwipeDistance;
        if (isLeftSwipe) nextImage();
        if (isRightSwipe) prevImage();
    };

    const nextImage = () => {
        if (!ad) return;
        const images = getImagesArray();
        setCurrentImgIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
    };

    const prevImage = () => {
        if (!ad) return;
        const images = getImagesArray();
        setCurrentImgIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
    };

    const getImagesArray = () => {
        if (!ad) return [];
        return ad.images && ad.images.length > 0 ? ad.images : [ad.image].filter(Boolean);
    };

    useEffect(() => {
        const fetchAd = async () => {
            try {
                let adData = null;
                let adDocRef = null;

                if (id) {
                    // Fetch by ID (Old/Legacy format)
                    const docRef = doc(db, "ads", id);
                    const docSnap = await getDoc(docRef);
                    if (docSnap.exists()) {
                        adData = { id: docSnap.id, ...docSnap.data() };
                        adDocRef = docRef;
                    }
                } else if (vendorSlug && adSlug) {
                    // Fetch by VendorSlug and AdSlug (New Corporate format)
                    const adsRef = collection(db, "ads");
                    const q = query(adsRef, where("vendorSlug", "==", vendorSlug), where("adSlug", "==", adSlug));
                    const querySnapshot = await getDocs(q);

                    if (!querySnapshot.empty) {
                        const snap = querySnapshot.docs[0];
                        adData = { id: snap.id, ...snap.data() };
                        adDocRef = snap.ref;
                    }
                }

                if (adData) {
                    setAd(adData);

                    // Fetch seller/vendor profile
                    let sellerData = null;
                    if (adData.userId && adData.userId !== 'anonymous') {
                        try {
                            const sellerDoc = await getDoc(doc(db, "users", adData.userId));
                            if (sellerDoc.exists()) {
                                sellerData = { id: sellerDoc.id, ...sellerDoc.data() };
                                setSeller(sellerData);
                            }
                        } catch (err) {
                            console.error("Error fetching seller:", err);
                        }
                    }

                    // If accessed via /ad/:id and seller is Corporate or Admin, redirect to slug URL
                    const vSlug = adData.vendorSlug || (sellerData && (sellerData.slug || (sellerData.role === 'admin' ? 'riyamaga' : '')));
                    const isSlugEligible = sellerData && (sellerData.userType === 'Corporate' || sellerData.role === 'admin');

                    if (id && isSlugEligible && vSlug) {
                        const generatedAdSlug = adData.adSlug || slugify(adData.title);

                        if (generatedAdSlug) {
                            // Backfill slugs in Firestore if missing
                            if (!adData.vendorSlug || !adData.adSlug) {
                                updateDoc(adDocRef, {
                                    vendorSlug: vSlug,
                                    adSlug: generatedAdSlug
                                }).catch(err => console.error("Error backfilling slugs:", err));
                            }

                            // Redirect to clean URL
                            navigate(`/${vSlug}/${generatedAdSlug}`, { replace: true });
                            return;
                        }
                    }

                    // Increment view count in background ONLY once
                    if (!hasViewed.current && adDocRef) {
                        hasViewed.current = true;
                        updateDoc(adDocRef, {
                            views: increment(1)
                        }).catch(err => console.error("Error incrementing views:", err));
                    }

                } else {
                    console.log("No such document!");
                }
            } catch (error) {
                console.error("Error fetching ad:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchAd();
    }, [id, vendorSlug, adSlug]);

    useEffect(() => {
        if (!showAdBanner) return;

        const adUrls = [
            "https://www.effectivegatecpm.com/ffevda195?key=8041eb5f8a219d4a8fc1c05e8e7fe485",
            "https://www.effectivegatecpm.com/uxrr8n9w?key=86eef292e9311cbaca68fece99b4a531"
        ];

        const popupTimer = setTimeout(() => {
            const randomUrl = adUrls[Math.floor(Math.random() * adUrls.length)];
            const popupWin = window.open(randomUrl, '_blank');
            if (!popupWin || popupWin.closed || typeof popupWin.closed === 'undefined') {
                setShowFallbackAd(true);
            }
        }, 5000);

        return () => clearTimeout(popupTimer);
    }, [id, vendorSlug, adSlug, showAdBanner]);

    useEffect(() => {
        if (!showAdBanner) return;

        if (showFallbackAd) {
            const script = document.createElement('script');
            script.src = "https://pl28752224.effectivegatecpm.com/29b2489288512e4b6960d9a821c43817/invoke.js";
            script.async = true;
            script.setAttribute("data-cfasync", "false");
            document.head.appendChild(script);

            return () => {
                if (document.head.contains(script)) {
                    document.head.removeChild(script);
                }
            };
        }
    }, [showFallbackAd, showAdBanner]);

    useEffect(() => {
        if (showFullImage) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }

        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [showFullImage]);

    if (loading) return <Spinner fullPage message="Loading ad details..." />;
    if (!ad) return <div className="error-container">Ad not found. <button onClick={() => navigate('/')}>Go Home</button></div>;

    const {
        title, price, location, description, category,
        vehicleType, vehicleCondition, vehicleMake, model, manufacturedYear,
        transmission, fuelType, engineCapacity, mileage,
        partCategory, depositAmount, rentalTerm, servicedVehicleTypes,
        options, images, image, userName, userPhone, createdAt, views
    } = ad;

    const allImages = images && images.length > 0 ? images : [image].filter(Boolean);
    const mainImg = allImages[currentImgIndex] || 'https://placehold.co/600x400/1e293b/ffffff?text=No+Image';

    const isCorporateSeller = seller?.userType === 'Corporate';
    const sellerDisplayName = isCorporateSeller ? (seller?.vendorName || seller?.fullName || userName) : (seller?.fullName || userName);

    // SEO computed values
    const pageTitle = `${title} | ${vehicleCondition || ''} ${vehicleMake || ''} ${model || ''} for Sale in ${location || 'Sri Lanka'} | RiyaMaga`;
    const metaDescription = [
        `Buy ${title} in ${location || 'Sri Lanka'}.`,
        vehicleCondition && `Condition: ${vehicleCondition}.`,
        vehicleMake && model && `${vehicleMake} ${model}`,
        manufacturedYear && `year ${manufacturedYear}.`,
        mileage && `Mileage: ${mileage} km.`,
        `Price: ${price || 'Negotiable'}.`,
        sellerDisplayName && `Sold by ${sellerDisplayName}.`,
        'Find best prices on RiyaMaga — Sri Lanka vehicle marketplace.'
    ].filter(Boolean).join(' ');

    const metaKeywords = [
        title, vehicleMake, model, vehicleType, vehicleCondition,
        location, `${vehicleMake} ${model} for sale Sri Lanka`,
        `buy ${vehicleType} Sri Lanka`, `${vehicleMake} price Sri Lanka`,
        'vehicle for sale Sri Lanka', 'RiyaMaga'
    ].filter(Boolean).join(', ');

    const canonicalUrl = `https://riyamaga.site${ad.vendorSlug && ad.adSlug ? `/${ad.vendorSlug}/${ad.adSlug}` : `/ad/${ad.id}`}`;

    // JSON-LD: Vehicle structured data for Google rich results
    const jsonLd = {
        "@context": "https://schema.org",
        "@type": "MotorizedVehicle",
        "name": title,
        "description": description || metaDescription,
        "image": allImages,
        "brand": vehicleMake ? { "@type": "Brand", "name": vehicleMake } : undefined,
        "model": model || undefined,
        "modelDate": manufacturedYear ? String(manufacturedYear) : undefined,
        "mileageFromOdometer": mileage ? {
            "@type": "QuantitativeValue",
            "value": mileage,
            "unitCode": "KMT"
        } : undefined,
        "fuelType": fuelType || undefined,
        "vehicleTransmission": transmission || undefined,
        "vehicleEngine": engineCapacity ? {
            "@type": "EngineSpecification",
            "engineDisplacement": {
                "@type": "QuantitativeValue",
                "value": engineCapacity,
                "unitCode": "CMQ"
            }
        } : undefined,
        "offers": {
            "@type": "Offer",
            "price": String(price || '').replace(/[^0-9.]/g, '') || '0',
            "priceCurrency": "LKR",
            "availability": "https://schema.org/InStock",
            "url": canonicalUrl,
            "seller": sellerDisplayName ? { "@type": "Person", "name": sellerDisplayName } : undefined
        },
        "vehicleCondition": vehicleCondition === 'New'
            ? "https://schema.org/NewCondition"
            : "https://schema.org/UsedCondition",
        "url": canonicalUrl
    };

    return (
        <div className="ad-details-page">
            <Helmet>
                <title>{pageTitle}</title>
                <meta name="description" content={metaDescription} />
                <meta name="keywords" content={metaKeywords} />
                <link rel="canonical" href={canonicalUrl} />

                {/* Open Graph */}
                <meta property="og:type" content="product" />
                <meta property="og:title" content={`${title} — ${vehicleMake || ''} ${model || ''} | RiyaMaga`} />
                <meta property="og:description" content={metaDescription} />
                <meta property="og:image" content={mainImg} />
                <meta property="og:url" content={canonicalUrl} />
                <meta property="og:site_name" content="RiyaMaga" />

                {/* Twitter Card */}
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content={`${title} | RiyaMaga`} />
                <meta name="twitter:description" content={metaDescription} />
                <meta name="twitter:image" content={mainImg} />

                {/* JSON-LD Structured Data */}
                <script type="application/ld+json">
                    {JSON.stringify(jsonLd)}
                </script>
            </Helmet>
            <div className="ad-details-content">
                <div className="ad-details-left-column">
                    {/* Left Column - Main Details & Description */}
                    <div className="ad-details-main">
                        {/* Gallery Section */}
                        <section className="ad-gallery-section">
                            <div
                                className="main-image-viewport"
                                onTouchStart={onTouchStart}
                                onTouchMove={onTouchMove}
                                onTouchEnd={onTouchEnd}
                                onClick={() => setShowFullImage(true)}
                                style={{ cursor: 'zoom-in' }}
                            >
                                <img
                                    src={mainImg}
                                    alt={`${title} - ${vehicleCondition || ''} ${vehicleMake || ''} ${model || ''} ${manufacturedYear || ''} for sale in ${location || 'Sri Lanka'} - Image ${currentImgIndex + 1}`}
                                    title={`${title} | ${vehicleMake || ''} ${model || ''} | RiyaMaga`}
                                    className="gallery-main-img"
                                />

                                {allImages.length > 1 && (
                                    <>
                                        <button className="gallery-nav-btn prev" onClick={prevImage}>
                                            <i className="fa-solid fa-chevron-left"></i>
                                        </button>
                                        <button className="gallery-nav-btn next" onClick={nextImage}>
                                            <i className="fa-solid fa-chevron-right"></i>
                                        </button>
                                        <div className="gallery-dots">
                                            {allImages.map((_, idx) => (
                                                <span
                                                    key={idx}
                                                    className={`dot ${currentImgIndex === idx ? 'active' : ''}`}
                                                    onClick={() => setCurrentImgIndex(idx)}
                                                />
                                            ))}
                                        </div>
                                    </>
                                )}
                            </div>
                            <div className="price-banner">
                                <span className="price-text">Rs. {price}</span>
                                <div className="price-share-actions">
                                    <button className="share-btn fb" onClick={(e) => { e.stopPropagation(); window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.href)}`, '_blank'); }} title="Share on Facebook">
                                        <i className="fa-brands fa-facebook-f"></i>
                                    </button>
                                    <button className="share-btn wa" onClick={(e) => { e.stopPropagation(); window.open(`https://wa.me/?text=${encodeURIComponent(`${title} - Rs. ${price} - Check it on RiyaMaga: ${window.location.href}`)}`, '_blank'); }} title="Share on WhatsApp">
                                        <i className="fa-brands fa-whatsapp"></i>
                                    </button>
                                    <button className="share-btn copy" onClick={(e) => {
                                        e.stopPropagation();
                                        navigator.clipboard.writeText(window.location.href);
                                    }} title="Copy Link">
                                        <i className="fa-solid fa-link"></i>
                                    </button>
                                </div>
                            </div>
                        </section>
                    </div>

                    {/* Description & Views - Positioned here for mobile reordering, but Grid handles desktop position */}
                    <div className="ad-details-description-area">
                        {/* Description Section */}
                        <section className="ad-section">
                            <h3 className="section-title-small">Description</h3>
                            <p className="description-text">{description}</p>
                        </section>

                        {/* Post Metadata */}
                        <div className="ad-meta-footer">
                            <div className="meta-stats">
                                <span><i className="fa-regular fa-eye"></i> {views || 0} views</span>
                                <span><i className="fa-regular fa-clock"></i> Posted: {createdAt?.toDate ? new Date(createdAt.toDate()).toISOString().split('T')[0] : 'Recently'}</span>
                            </div>
                            <button className="btn-report"><i className="fa-solid fa-flag"></i> Report this ad</button>
                        </div>
                    </div>
                </div>

                {/* Right Column - Overview & Specs */}
                <aside className="ad-details-sidebar">
                    {/* Quick Overview */}
                    <div className="ad-quick-info">
                        <h2 className="ad-main-title">{vehicleMake} {model}</h2>
                        <div className="quick-meta-items">
                            <div className="meta-item"><i className="fa-solid fa-location-dot"></i> {location}</div>
                            <div className="meta-item"><i className="fa-solid fa-gauge"></i> {mileage} km</div>
                            <div className="meta-item"><i className="fa-solid fa-calendar"></i> {manufacturedYear}</div>
                        </div>
                    </div>

                    {/* Seller / Vendor Info Card */}
                    <div
                        className="seller-vendor-card"
                        onClick={() => {
                            if (isCorporateSeller && ad.userId && ad.userId !== 'anonymous') {
                                navigate(`/${seller?.slug || ad.userId}`);
                            }
                        }}
                        style={{ cursor: (isCorporateSeller && ad.userId && ad.userId !== 'anonymous') ? 'pointer' : 'default' }}
                    >
                        <div className="seller-vendor-left">
                            {isCorporateSeller && seller?.logo ? (
                                <img
                                    src={seller.logo}
                                    alt={`${sellerDisplayName} - Vehicle Dealer Sri Lanka`}
                                    title={sellerDisplayName}
                                    className="seller-vendor-avatar-img"
                                />
                            ) : (
                                <div className="seller-vendor-avatar-placeholder">
                                    <i className={isCorporateSeller ? "fa-solid fa-building" : "fa-solid fa-user"}></i>
                                </div>
                            )}
                        </div>
                        <div className="seller-vendor-right">
                            <div className="seller-vendor-name-row">
                                <span className="seller-vendor-name">{sellerDisplayName || 'Unknown'}</span>
                                {isCorporateSeller && (
                                    <span className="seller-vendor-badge">
                                        <i className="fa-solid fa-check-circle"></i> Corporate
                                    </span>
                                )}
                            </div>
                            {isCorporateSeller && (
                                <span className="seller-vendor-cta">
                                    View Vendor Profile <i className="fa-solid fa-arrow-right"></i>
                                </span>
                            )}
                        </div>
                    </div>

                    {/* Action Bar (Visible in Sidebar on Desktop) */}
                    <div className="sidebar-action-bar">
                        <a href={`tel:${userPhone}`} className="action-btn call-btn">
                            <i className="fa-solid fa-phone"></i> Call Seller
                        </a>
                        <a href={`https://wa.me/${formatWhatsAppNumber(userPhone)}`} target="_blank" rel="noopener noreferrer" className="action-btn whatsapp-btn">
                            <i className="fa-brands fa-whatsapp"></i> Chat on WhatsApp
                        </a>
                    </div>

                    {/* Safety Alert */}
                    <div className="ad-safety-alert">
                        <i className="fa-solid fa-triangle-exclamation"></i>
                        <p>වාහන පරික්ෂා කිරීමට පෙර මුදල් ගෙවීමෙන් වලකින්න</p>
                    </div>

                    {showAdBanner && <AdBanner />}

                    {/* Specifications Section */}
                    <section className="ad-section specs-section">
                        <h3 className="section-title-small">Specifications</h3>
                        <div className="specs-table">
                            {vehicleMake && <div className="specs-row"><span>{category === 'Parts' ? 'Brand' : 'Make'}</span><strong>{vehicleMake}</strong></div>}
                            {model && <div className="specs-row"><span>Model</span><strong>{model}</strong></div>}
                            {manufacturedYear && <div className="specs-row"><span>Year</span><strong>{manufacturedYear}</strong></div>}
                            {vehicleCondition && <div className="specs-row"><span>Condition</span><strong>{vehicleCondition}</strong></div>}
                            {partCategory && <div className="specs-row"><span>Part Category</span><strong>{partCategory}</strong></div>}
                            {vehicleType && <div className="specs-row"><span>{category === 'Parts' ? 'Used In' : 'Body Type'}</span><strong>{vehicleType}</strong></div>}
                            {transmission && transmission !== 'None' && <div className="specs-row"><span>Transmission</span><strong>{transmission}</strong></div>}
                            {fuelType && fuelType !== 'None' && <div className="specs-row"><span>Fuel Type</span><strong>{fuelType}</strong></div>}
                            {engineCapacity && <div className="specs-row"><span>Engine (CC)</span><strong>{engineCapacity}</strong></div>}
                            {mileage && <div className="specs-row"><span>Mileage (km)</span><strong>{mileage}</strong></div>}
                            {depositAmount && <div className="specs-row"><span>Deposit</span><strong>Rs. {depositAmount}</strong></div>}
                            {rentalTerm && <div className="specs-row"><span>Rental Term</span><strong>{rentalTerm}</strong></div>}
                            {servicedVehicleTypes && servicedVehicleTypes.length > 0 && (
                                <div className="specs-row">
                                    <span>Serviced Types</span>
                                    <strong>{servicedVehicleTypes.join(', ')}</strong>
                                </div>
                            )}
                        </div>
                    </section>

                    {/* Features Section */}
                    {options && Object.values(options).some(val => val) && (
                        <section className="ad-section features-section">
                            <h3 className="section-title-small">Features</h3>
                            <div className="features-badges">
                                {options.airCondition && <span className="feature-badge">AIR CONDITION</span>}
                                {options.powerSteering && <span className="feature-badge">POWER STEERING</span>}
                                {options.powerMirror && <span className="feature-badge">POWER MIRROR</span>}
                                {options.powerWindow && <span className="feature-badge">POWER WINDOW</span>}
                            </div>
                        </section>
                    )}
                </aside>
            </div>

            {/* Action Buttons (Sticky - Mobile Only) */}
            <div className="sticky-action-bar mobile-only">
                <a href={`tel:${userPhone}`} className="action-btn call-btn">
                    <i className="fa-solid fa-phone"></i> Call
                </a>
                <a href={`https://wa.me/${formatWhatsAppNumber(userPhone)}`} target="_blank" rel="noopener noreferrer" className="action-btn whatsapp-btn">
                    <i className="fa-brands fa-whatsapp"></i> WhatsApp
                </a>
            </div>

            {/* Full Screen Image Modal */}
            {showFullImage && (
                <div className="full-image-modal" onClick={() => setShowFullImage(false)}>
                    <button className="modal-close-btn" onClick={() => setShowFullImage(false)}>
                        <i className="fa-solid fa-xmark"></i>
                    </button>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <img
                            src={mainImg}
                            alt={`${title} - ${vehicleCondition || ''} ${vehicleMake || ''} ${model || ''} for sale in ${location || 'Sri Lanka'} - Full view`}
                            title={`${title} | RiyaMaga`}
                            className="modal-img"
                        />
                        {allImages.length > 1 && (
                            <>
                                <button className="modal-nav-btn prev" onClick={prevImage}>
                                    <i className="fa-solid fa-chevron-left"></i>
                                </button>
                                <button className="modal-nav-btn next" onClick={nextImage}>
                                    <i className="fa-solid fa-chevron-right"></i>
                                </button>
                            </>
                        )}
                    </div>
                </div>
            )}

            {/* Fallback Ad Modal (If Popup Blocked) */}
            {showAdBanner && showFallbackAd && (
                <div className="full-image-modal" style={{ zIndex: 9999, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-start', paddingTop: '2rem', paddingBottom: '2rem', overflowY: 'auto' }}>
                    <div className="modal-backdrop" onClick={() => setShowFallbackAd(false)} style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.85)', zIndex: 9998 }}></div>
                    <div style={{ position: 'relative', background: '#fff', padding: '1.5rem', borderRadius: '12px', textAlign: 'center', width: '90%', maxWidth: '400px', zIndex: 10000, margin: 'auto' }}>
                        <button className="close-btn" onClick={() => setShowFallbackAd(false)} style={{ border: 'none', background: '#eee', position: 'absolute', top: '-10px', right: '-10px', width: '30px', height: '30px', borderRadius: '50%', fontSize: '1.2rem', cursor: 'pointer', zIndex: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 5px rgba(0,0,0,0.2)' }}>
                            <i className="fa-solid fa-xmark"></i>
                        </button>
                        <p style={{ margin: '0 0 1rem 0', fontWeight: 'bold', fontSize: '1rem', paddingRight: '1rem' }}>Please support us by viewing this ad</p>
                        <div id="container-29b2489288512e4b6960d9a821c43817"></div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdDetails;
